import { type ReactNode, useEffect, useState } from "react";

import { cn } from "@/lib/utils";

/**
 * How long content may take to arrive before a skeleton is worth showing. Under this,
 * the swap is faster than the eye resolves it, so a placeholder reads as a flicker
 * rather than as feedback — most soft navigations serve from cache and land here.
 */
const SHOW_AFTER_MS = 150;

/**
 * Once a skeleton is on screen it stays for at least this long. Without it, data that
 * arrives just past SHOW_AFTER_MS produces the same flicker the delay exists to avoid.
 */
const MIN_VISIBLE_MS = 300;

type SkeletonGateProps = {
  pending: boolean;
  skeleton: ReactNode;
  children: ReactNode;
  className?: string;
};

/**
 * Holds a page's skeleton until its content is both ready and worth swapping in.
 *
 * Route loaders no longer block on content, so pages render before their sections have
 * data. Swapping straight from placeholder to content on the same frame is what reads as
 * jank: a full-screen replacement with no transition, sometimes after a placeholder that
 * was only visible for a few frames. This gates on time as well as readiness, and fades
 * the arriving content in.
 */
export const SkeletonGate = ({ pending, skeleton, children, className }: SkeletonGateProps) => {
  const [visibleSince, setVisibleSince] = useState<number | null>(null);
  const [holding, setHolding] = useState(false);

  useEffect(() => {
    if (!pending) return;
    const timer = setTimeout(() => setVisibleSince(Date.now()), SHOW_AFTER_MS);
    return () => clearTimeout(timer);
  }, [pending]);

  useEffect(() => {
    if (pending || visibleSince === null) return;
    const remaining = MIN_VISIBLE_MS - (Date.now() - visibleSince);
    if (remaining <= 0) {
      setHolding(false);
      return;
    }
    setHolding(true);
    const timer = setTimeout(() => setHolding(false), remaining);
    return () => clearTimeout(timer);
  }, [pending, visibleSince]);

  if (pending || holding) {
    return visibleSince === null ? null : <>{skeleton}</>;
  }

  // Content that arrived before any skeleton was shown has nothing to fade from, and
  // fading it in would only delay a page the user never waited for.
  if (visibleSince === null) return <>{children}</>;

  return (
    <div className={cn("duration-200 animate-in fade-in motion-reduce:animate-none", className)}>
      {children}
    </div>
  );
};
